import React from 'react';
import {
  Card,
  Tag,
  Timeline,
  Alert,
  Spin,
  Button,
  Descriptions,
  Empty,
  Message
} from '@arco-design/web-react';
import {
  IconCheckCircle,
  IconCloseCircle,
  IconClockCircle,
  IconRefresh
} from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../common/helpers/dateHelpers';
import { useOnboardingStatus } from './hooks/useOnboardingData';
import OnboardingLayout from './OnboardingLayout';
import './OnboardingStatusPage.css';

const TimelineItem = Timeline.Item;
const ADMIN_ROLES = new Set(['admin']);
const CORPORATE_ROLES = new Set(['corporate_contact']);

function OnboardingStatusPage() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();

  const {
    data: status,
    isLoading: loading,
    error: queryError,
    refetch: loadStatus
  } = useOnboardingStatus();

  const getStatusConfig = (submissionStatus) => {
    const configs = {
      'draft': {
        color: 'gray',
        icon: <IconClockCircle />,
        title: t('status.draft.title'),
        description: t('status.draft.description')
      },
      'submitted': {
        color: 'orange',
        icon: <IconClockCircle />,
        title: t('status.submitted.title'),
        description: t('status.submitted.description')
      },
      'approved': {
        color: 'green',
        icon: <IconCheckCircle />,
        title: t('status.approved.title'),
        description: t('status.approved.description')
      },
      'rejected': {
        color: 'red',
        icon: <IconCloseCircle />,
        title: t('status.rejected.title'),
        description: t('status.rejected.description')
      },
      'changes_requested': {
        color: 'blue',
        icon: <IconClockCircle />,
        title: t('status.changesRequested.title'),
        description: t('status.changesRequested.description')
      }
    };

    return configs[submissionStatus] || configs['submitted'];
  };

  const getValidationStatusTag = (validationStatus) => {
    const configs = {
      'Pendiente': { color: 'orange', text: t('status.validationTags.pending') },
      'Validado': { color: 'green', text: t('status.validationTags.validated') },
      'Rechazado': { color: 'red', text: t('status.validationTags.rejected') },
      'Requiere Cambios': { color: 'blue', text: t('status.validationTags.changesRequired') }
    };

    const config = configs[validationStatus] || configs['Pendiente'];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getStatusLabel = (submissionStatus) => {
    const labels = {
      draft: t('status.labels.draft'),
      submitted: t('status.labels.submitted'),
      in_review: t('status.labels.inReview'),
      approved: t('status.labels.approved'),
      rejected: t('status.labels.rejected'),
      changes_requested: t('status.labels.changesRequested')
    };

    return labels[submissionStatus] || submissionStatus;
  };

  const buildProgressSteps = () => {
    const identity = status?.identity_data || {};
    const teacher = status?.role_specific_data || {};
    const isAdmin = ADMIN_ROLES.has(status?.role_name);
    const isCorporate = CORPORATE_ROLES.has(status?.role_name);

    const isIdentityComplete = [
      'first_name',
      'last_name',
      'date_of_birth',
      'government_id',
      'government_id_type',
      'phone',
      'address',
      'city',
      'state',
      'postal_code',
      'country'
    ].every((field) => identity[field]);

    const isCorporateIdentityComplete = [
      'first_name',
      'last_name',
      'date_of_birth',
      'government_id',
      'government_id_type',
      'phone',
      'contact_email'
    ].every((field) => identity[field]);

    const degrees = Array.isArray(teacher.degrees) ? teacher.degrees : [];
    const hasAcademic = degrees.length > 0 && degrees.every((degree) => (
      degree.level && degree.institution && degree.field
      && ((degree.graduation_status || 'graduado') === 'cursando' || degree.graduation_year)
    ));
    const sniMissing = teacher.is_sni_member && !teacher.sni_level;
    const hasProfessional = isAdmin
      ? (teacher.years_work_experience !== undefined && teacher.years_work_experience !== null)
      : (teacher.years_teaching_experience !== undefined && teacher.years_teaching_experience !== null);
    const hasAvailability = Array.isArray(teacher.availability_schedule)
      && teacher.availability_schedule.length > 0
      && teacher.max_hours_per_week;
    const reviewComplete = status?.status && status.status !== 'draft';

    if (isCorporate) {
      const hasCorporate = !!teacher.company_legal_name
        && !!teacher.rfc
        && !!teacher.fiscal_address
        && !!teacher.fiscal_city
        && !!teacher.fiscal_state
        && !!teacher.fiscal_postal_code
        && !!teacher.fiscal_country;
      const hasOperator = Array.isArray(teacher.operator_contacts)
        && teacher.operator_contacts.length > 0
        && teacher.operator_contacts.every((contact) => (
          contact.full_name && contact.email && contact.phone
        ));
      return [
        { key: 'identity', label: t('status.steps.representativeData'), completed: isCorporateIdentityComplete },
        { key: 'corporate', label: t('status.steps.corporateData'), completed: hasCorporate },
        { key: 'operator', label: t('status.steps.programOperator'), completed: hasOperator },
        { key: 'review', label: t('status.steps.reviewAndSubmit'), completed: reviewComplete }
      ];
    }

    if (isAdmin) {
      return [
        { key: 'identity', label: t('status.steps.personalData'), completed: isIdentityComplete },
        { key: 'academic', label: t('status.steps.academicInfo'), completed: hasAcademic },
        { key: 'professional', label: t('status.steps.professionalProfile'), completed: hasProfessional },
        { key: 'review', label: t('status.steps.reviewAndSubmit'), completed: reviewComplete }
      ];
    }

    return [
      { key: 'identity', label: t('status.steps.personalData'), completed: isIdentityComplete },
      { key: 'academic', label: t('status.steps.academicInfo'), completed: hasAcademic && !sniMissing },
      { key: 'professional', label: t('status.steps.professionalProfile'), completed: hasProfessional },
      { key: 'availability', label: t('status.steps.availability'), completed: hasAvailability },
      { key: 'review', label: t('status.steps.reviewAndSubmit'), completed: reviewComplete }
    ];
  };

  if (loading) {
    return (
      <OnboardingLayout>
        <div className="status-loading">
          <Spin size={40} />
          <p>{t('status.loading')}</p>
        </div>
      </OnboardingLayout>
    );
  }

  if (queryError) {
    return (
      <OnboardingLayout>
        <Empty description={t('status.loadError')} />
      </OnboardingLayout>
    );
  }

  if (!status || !status.submission_id) {
    return (
      <OnboardingLayout>
        <Empty description={t('status.noInfo')} />
      </OnboardingLayout>
    );
  }

  const statusConfig = getStatusConfig(status.status);
  const rejectionReason = status?.rejection_reason?.trim();
  const reviewNotes = status?.review_notes?.trim();
  const requestedChanges = Array.isArray(status?.requested_changes) ? status.requested_changes : [];
  const pendingChanges = requestedChanges.filter((change) => !change.resolved);
  const hasPendingChanges = pendingChanges.length > 0;
  const createdAt = status?.created_at || null;
  const reviewedAt = status?.reviewed_at || null;
  const submittedAt = status?.submitted_at || null;
  const registrationStartedAt = createdAt || submittedAt || null;
  const formCompletedAt = submittedAt || null;
  const hasCorrectionRequest = status.status === 'changes_requested'
    || (status.status === 'submitted' && !!reviewNotes);
  const hasCorrectionSent = hasCorrectionRequest
    && status.status === 'submitted'
    && reviewedAt
    && submittedAt
    && new Date(submittedAt) > new Date(reviewedAt);
  const showFinalReview = hasCorrectionSent
    && ['submitted', 'in_review'].includes(status.status);
  const reviewInProgressLabel = ['submitted', 'in_review'].includes(status.status)
    ? t('status.inProgress')
    : t('status.pending');

  const formatChangeLabel = (change) => {
    const sectionLabels = {
      personal: t('status.sections.personal'),
      academic: t('status.sections.academic'),
      professional: t('status.sections.professional'),
      sni: t('status.sections.sni'),
      availability: t('status.sections.availability'),
      documents: t('status.sections.documents')
    };
    const stepLabels = {
      identity: t('status.sections.identity'),
      teacher_details: t('status.sections.teacherDetails'),
      documents: t('status.sections.documents')
    };
    const sectionLabel = sectionLabels[change.section] || stepLabels[change.step];
    if (sectionLabel) {
      return `${sectionLabel} - ${change.label}`;
    }
    return change.label;
  };

  const buildTimelineEvents = () => {
    const baseReviewValue = reviewedAt
      ? formatDateTime(reviewedAt)
      : reviewInProgressLabel;
    const baseReviewStatus = reviewedAt
      ? 'done'
      : (reviewInProgressLabel === t('status.inProgress') ? 'in_progress' : 'pending');

    const baseEvents = [
      {
        key: 'registration_start',
        label: t('status.timeline.registrationStart'),
        value: registrationStartedAt ? formatDateTime(registrationStartedAt) : t('status.pending'),
        status: registrationStartedAt ? 'done' : 'pending'
      },
      {
        key: 'form_completed',
        label: t('status.timeline.formCompleted'),
        value: formCompletedAt ? formatDateTime(formCompletedAt) : t('status.pending'),
        status: formCompletedAt ? 'done' : 'pending'
      },
      ...buildProgressSteps().map((step) => ({
        key: `form_step_${step.key}`,
        label: step.label,
        status: step.completed ? 'done' : 'pending',
        tag: step.completed ? t('status.completed') : t('status.pending'),
        tagColor: step.completed ? 'green' : 'gray',
        isSubstep: true
      })),
      {
        key: 'admin_review',
        label: t('status.timeline.adminReview'),
        value: baseReviewValue,
        status: baseReviewStatus
      }
    ];

    const dynamicEvents = [];

    if (hasCorrectionRequest) {
      dynamicEvents.push({
        key: 'correction_requested',
        label: t('status.timeline.correctionRequested'),
        value: reviewedAt ? formatDateTime(reviewedAt) : t('status.pending'),
        status: reviewedAt ? 'done' : 'pending',
        tone: 'warning'
      });
    }

    if (hasCorrectionSent) {
      dynamicEvents.push({
        key: 'correction_resolved',
        label: t('status.timeline.correctionResolved'),
        value: submittedAt ? formatDateTime(submittedAt) : t('status.pending'),
        status: submittedAt ? 'done' : 'pending'
      });
    }

    if (showFinalReview) {
      dynamicEvents.push({
        key: 'admin_review_followup',
        label: t('status.timeline.adminReview'),
        value: t('status.inProgress'),
        status: 'in_progress'
      });
    }

    if (status.status === 'approved') {
      dynamicEvents.push({
        key: 'submission_approved',
        label: t('status.timeline.applicationApproved'),
        value: reviewedAt ? formatDateTime(reviewedAt) : t('status.pending'),
        status: reviewedAt ? 'done' : 'pending',
        tone: 'success'
      });
    }

    if (status.status === 'rejected') {
      dynamicEvents.push({
        key: 'submission_rejected',
        label: t('status.timeline.applicationRejected'),
        value: reviewedAt ? formatDateTime(reviewedAt) : t('status.pending'),
        status: reviewedAt ? 'done' : 'pending',
        tone: 'danger'
      });
    }

    return [...baseEvents, ...dynamicEvents];
  };

  const timelineEvents = buildTimelineEvents();

  const renderTimelineDot = (event) => {
    if (event.tone === 'warning') {
      return <IconClockCircle style={{ color: 'var(--color-warning-6)' }} />;
    }
    if (event.tone === 'danger') {
      return <IconCloseCircle style={{ color: 'var(--color-danger-6)' }} />;
    }
    if (event.tone === 'success') {
      return <IconCheckCircle style={{ color: 'var(--color-success-6)' }} />;
    }
    if (event.status === 'done') {
      return <IconCheckCircle style={{ color: 'var(--color-success-6)' }} />;
    }
    return <IconClockCircle style={{ color: 'var(--color-neutral-6)' }} />;
  };

  return (
    <OnboardingLayout>
      <div className="onboarding-status-page">
        <Card className="status-main-card">
          <div className="status-header">
            <div className="status-icon" style={{ color: `var(--color-${statusConfig.color}-6)` }}>
              {statusConfig.icon}
            </div>
            <div className="status-info">
              <h2>{statusConfig.title}</h2>
              <p>{statusConfig.description}</p>
              <Tag color={statusConfig.color} size="large">
                {getStatusLabel(status.status)}
              </Tag>
            </div>
          </div>

          <Descriptions
            column={2}
            data={[
              {
                label: t('status.requestId'),
                value: `#${status.submission_id}`
              },
              status.submitted_at
                ? {
                  label: t('status.sentLabel'),
                  value: formatDateTime(status.submitted_at)
                }
                : {
                  label: t('status.sentLabel'),
                  value: t('status.pending')
                }
            ]}
            style={{ marginTop: 24 }}
          />

          {status.status === 'rejected' && (
            <Alert
              type="error"
              showIcon
              content={
                <div>
                  <strong>{t('status.rejectionReason')}</strong>{' '}
                  {rejectionReason || t('status.noReasonSpecified')}
                </div>
              }
              style={{ marginTop: 16 }}
            />
          )}
        </Card>

        <Card
          title={t('status.processProgress')}
          className="validation-card"
          extra={
            <Button
              type="text"
              icon={<IconRefresh />}
              onClick={() => loadStatus()}
            >
              {t('status.refresh')}
            </Button>
          }
        >
          <Timeline>
            {timelineEvents.map((event) => (
              <TimelineItem
                key={event.key}
                className={event.isSubstep ? 'onboarding-status-substep-item' : undefined}
                dot={renderTimelineDot(event)}
              >
                <div className={`timeline-content${event.isSubstep ? ' onboarding-status-substep' : ''}`}>
                  <strong>{event.label}</strong>
                  <div>
                    {event.tag ? (
                      <Tag color={event.tagColor}>{event.tag}</Tag>
                    ) : (
                      event.value
                    )}
                  </div>
                </div>
              </TimelineItem>
            ))}
          </Timeline>
        </Card>

        {status.status === 'changes_requested' && (
          <Card className="actions-card">
            <Alert
              type="warning"
              content={
                hasPendingChanges
                  ? t('status.changesAlert')
                  : t('status.changesResolved')
              }
            />
            {hasPendingChanges ? (
              <div className="changes-list">
                <div className="changes-title">{t('status.requestedChanges')}</div>
                <ul>
                  {pendingChanges.map((change, index) => (
                    <li key={`${change.field}-${index}`}>
                      <strong>{formatChangeLabel(change)}:</strong> {change.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="changes-empty">
                {t('status.noPendingChanges')} {reviewNotes ? t('status.notes', { notes: reviewNotes }) : ''}
              </div>
            )}
            <Button
              type="primary"
              size="large"
              onClick={() => navigate('/onboarding/wizard', { state: { allowOnboardingEdit: true } })}
              style={{ marginTop: 16 }}
            >
              {t('status.editInfo')}
            </Button>
          </Card>
        )}

        {status.status === 'approved' && (
          <Card className="actions-card">
            <Alert
              type="success"
              content={t('status.approvedAlert')}
            />
            <Button
              type="primary"
              size="large"
              onClick={() => navigate('/dashboard')}
              style={{ marginTop: 16 }}
            >
              {t('status.goToDashboard')}
            </Button>
          </Card>
        )}
      </div>
    </OnboardingLayout>
  );
}

export default OnboardingStatusPage;
